/**
 * A menu (Speisekarte): a choice of dishes from the catalogue, with prices and an order of its own
 * (docs/01-architektur.md, Datenmodell). Either one plain list (a tapas bar) or sections: starters,
 * mains, desserts, and set menus with one price for all courses. Names, descriptions, tags and
 * allergens come from the catalogue when the menu is shown (S3); the price belongs to the menu.
 */
import { cleanWindow } from "./hours.ts";
import type { Window } from "./hours.ts";
import type { CardLang } from "./card-lang.ts";
import { sortCodes } from "./allergens.ts";
import type { Allergen } from "./allergens.ts";
import { categoryRank, secondCategory, topCategory } from "./categories.ts";
import { isDietTag, isDrink, mlOf, sameLabel } from "./dishes.ts";
import type { Dish } from "./dishes.ts";
import { MAX_PRICE } from "./money.ts";
import { uuidv7 } from "./ids.ts";
import { cleanOwnPages } from "./own-pages.ts";
import type { MenuOwnPages } from "./own-pages.ts";
import type { CardText } from "./texts.ts";
import { scaleOf } from "./scale.ts";
import { unitSpaced } from "./design.ts";
import type { MenuScale } from "./scale.ts";

export type Structure = "list" | "sections";
export type Layout = "classic" | "compact" | "columns";
export const LAYOUTS: readonly Layout[] = ["classic", "compact", "columns"];
export const LAYOUT_LABELS: Record<Layout, string> = { classic: "Klassisch", compact: "Kompakt", columns: "Zweispaltig" };
export const LAYOUT_HINTS: Record<Layout, string> = {
  classic: "Name und Preis, darunter die Beschreibung",
  compact: "eine Zeile je Speise, für lange Listen und die Tafel",
  columns: "klassisch in zwei Spalten, passt viel auf eine Seite",
};

export interface MenuItem {
  /** The id of a dish, or of a text of the catalogue (S14): a text has no price and no note. */
  dish: string;
  /** Price on this menu in cents; null in a set menu (the set has the price) or when it is not shown. */
  price: number | null;
  /** Said only on this menu: „nur am Wochenende“. */
  note: string;
  /** The further prices of the dish on this menu (S19), by their labels; a label not here has the catalogue's price. */
  variants?: MenuVariant[];
  /** In a set menu (S32): an alternative to the item before it, „oder“ between them; never on the first. */
  or?: true;
  /** In a set menu (S32): what the dish costs on top of the set's price, in cents („Rinderfilet + 8,00“). */
  surcharge?: number;
  /** In a set menu (S32), on the first item of a course: the course's title („Vorspeise“, „Hauptgang“). */
  course?: string;
  /** In a set menu (S52): a drink to the course before it, below its dishes; never on the first, never with „oder“ or a title. */
  paired?: true;
  /** A subsection of the card (S44): an entry of its own (`dish` „sub-…“, no dish) with its title („Vom Kalb“); the items after it belong to it. */
  sub?: string;
}

/** The ids of subsections (S44) begin so: no dish or text of the catalogue has them. */
export const SUB_PREFIX = "sub-";
const SUB_ID = /^sub-[\w-]{1,64}$/;
/** Whether an entry of a section is a subsection (S44), not a dish or text. */
export const isSubsection = (i: Pick<MenuItem, "dish">) => i.dish.startsWith(SUB_PREFIX);
/** A new subsection (S44), its title still to type. */
export const newSubsection = (title = "", at = Date.now()): MenuItem => ({ dish: `${SUB_PREFIX}${uuidv7(at)}`, price: null, note: "", sub: title });

export interface MenuVariant {
  label: string;
  price: number | null;
}

/** A further price of a set menu (S22): „Korrespondierende Weinbegleitung 79 €“, „6 Gänge 129 €“. */
export interface SetVariant {
  label: string;
  price: number | null;
  allergens: string[];
}

export interface MenuSection {
  id: string;
  title: string;
  note: string;
  /** "set": a set menu, one price for all its courses, which show no price of their own. */
  kind: "group" | "set";
  price: number | null;
  items: MenuItem[];
  /** In the part of the drinks (S22); a section without it is one of the food. */
  part?: "drinks";
  /**
   * A set menu (S22): what its price is for („4 Gänge“), as a dish's price label. A section of the food
   * (S46): what all its prices are for, once over their column („30 cm“ over the pizzas).
   */
  priceLabel?: string;
  /** A set menu (S22): further prices below its courses, as a dish's (S19). */
  variants?: SetVariant[];
  /**
   * A set menu with courses to choose from (S32): its dishes with their numbers of the catalogue,
   * whatever the menu's own switch says („ich nehme die 1, 5, 23 und 6“). A fixed one never has any.
   */
  numbers?: true;
  /** Print, PDF and Word (S43): the section begins on a new page. Only food; the drinks have their own switch. */
  newPage?: true;
  /**
   * Zeiten im Netz (S62, Stufe 1): a temporary section, on the net only at these times. On paper
   * with the time under its title, without it („plain“) or not at all („hide“; Martin, F2).
   */
  web?: SectionTimes;
}

/** When a temporary section (S62) is on the net, and how it is on paper; `print` missing: with its time. */
export type SectionTimes = Window & { print?: "plain" | "hide" };

/** A section's times as the app sends them, or undefined. */
export function sectionTimesOf(v: unknown): SectionTimes | undefined {
  const w = cleanWindow(v);
  if (!w) return undefined;
  const print = (v as Record<string, unknown>).print;
  return { ...w, ...(print === "plain" || print === "hide" ? { print } : {}) };
}

/** The two parts of a menu (S22): first the food, then the drinks. */
export type Part = "food" | "drinks";

/** How the part of the drinks is built and looks (S22); the food's are the menu's own fields. */
export interface DrinksPart {
  structure: Structure;
  layout: Layout;
  showNumbers: boolean;
  /** Print, PDF, Word: the drinks begin on a new page. */
  newPage: boolean;
}

export interface Menu {
  id: string;
  title: string;
  subtitle: string;
  note: string;
  structure: Structure;
  layout: Layout;
  showNumbers: boolean;
  /** The tags that appear on the card (lower case); null: the diet tags (vegan, vegetarisch …). */
  showTags: string[] | null;
  sections: MenuSection[];
  /** Its style (S15), by id; empty: the house style. */
  style: string;
  /** The part of the drinks (S22); a menu without drinks has none. */
  drinks?: DrinksPart;
  /** Its size as a whole (T14) in print, PDF and Word; none: the style's. */
  scale?: MenuScale;
  /** The house's own pages before and after it (S50), in print, PDF and Word; none: none. */
  ownPages?: MenuOwnPages;
  createdAt: number;
  updatedAt: number;
  createdBy: string | null;
  version: number;
}

/** What the app sends when it saves a menu. */
export type MenuInput = Pick<Menu, "title" | "subtitle" | "note" | "structure" | "layout" | "showNumbers" | "showTags" | "sections"> & { style?: string; drinks?: DrinksPart; scale?: MenuScale; ownPages?: MenuOwnPages };

export const MENU_LIMITS = { title: 120, subtitle: 160, note: 600, sectionTitle: 80, sections: 40, items: 400, itemNote: 120, course: 60, sub: 60 } as const;

const oneLine = (s: unknown, max: number) => (typeof s === "string" ? s.normalize("NFC").replace(/\s+/g, " ").trim().slice(0, max) : "");
const priceOf = (v: unknown): number | null => (typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= MAX_PRICE ? v : null);

/** „Speisekarte 21.10.2026“ */
export const defaultTitle = (at: number) => `Speisekarte ${new Date(at).toLocaleDateString("de-DE", { timeZone: "Europe/Berlin" })}`;

export function emptySection(title = "", kind: MenuSection["kind"] = "group", part: Part = "food"): MenuSection {
  return { id: uuidv7(), title, note: "", kind, price: null, items: [], ...(part === "drinks" ? { part: "drinks" as const } : {}) };
}

export const partOf = (s: Pick<MenuSection, "part">): Part => (s.part === "drinks" ? "drinks" : "food");

/** The part of the drinks a menu begins with when its first drink comes: grouped, compact, without numbers. */
export const DEFAULT_DRINKS: DrinksPart = { structure: "sections", layout: "compact", showNumbers: false, newPage: false };

/** The part of the drinks as the menu made last with drinks had it (as new menus learn, Martin 25.10.2026). */
export function drinksLike(menus: readonly Pick<Menu, "createdAt" | "drinks">[]): DrinksPart {
  const last = menus.filter((m) => m.drinks).reduce<(typeof menus)[number] | null>((a, m) => (!a || m.createdAt > a.createdAt ? m : a), null);
  return last?.drinks ? { ...last.drinks } : { ...DEFAULT_DRINKS };
}

/**
 * The menu on the net with the drinks of another menu (S22, „Getränke aus Speisekarte …“): its own
 * food, the other's drinks; without another, the menu as it is.
 */
export function withDrinksOf<M extends Pick<Menu, "sections"> & { drinks?: DrinksPart }>(menu: M, other: (Pick<Menu, "sections"> & { drinks?: DrinksPart }) | null): M {
  if (!other) return menu;
  const next = { ...menu, sections: [...menu.sections.filter((s) => partOf(s) === "food"), ...other.sections.filter((s) => partOf(s) === "drinks")] };
  if (other.drinks) next.drinks = other.drinks;
  else delete next.drinks;
  return next;
}

/** Whether a menu has drinks on it (S22). */
export const hasDrinks = (m: Pick<Menu, "sections">) => m.sections.some((s) => s.part === "drinks" && s.items.length > 0);

/** The sections and the build of one part, as a menu of its own (S22). */
export function partMenu(menu: Pick<MenuInput, "sections" | "structure" | "drinks">, part: Part): { structure: Structure; sections: MenuSection[] } {
  return { structure: part === "food" ? menu.structure : (menu.drinks?.structure ?? DEFAULT_DRINKS.structure), sections: menu.sections.filter((s) => partOf(s) === part) };
}

/**
 * Works on one part of a menu with a function made for a whole menu (sorting, sections from
 * categories, back to a list): the other part stays as it is, the food first, then the drinks.
 */
export function onPart<M extends Pick<MenuInput, "sections" | "structure" | "drinks">>(menu: M, part: Part, fn: (sub: { structure: Structure; sections: MenuSection[] }) => { structure: Structure; sections: MenuSection[] }): M {
  const out = fn(partMenu(menu, part));
  const mark = (x: MenuSection): MenuSection => {
    if (part === "drinks") return { ...x, part: "drinks" };
    const rest = { ...x };
    delete rest.part;
    return rest;
  };
  const sections = out.sections.map(mark);
  const food = part === "food" ? sections : menu.sections.filter((x) => partOf(x) === "food");
  const drinks = part === "drinks" ? sections : menu.sections.filter((x) => partOf(x) === "drinks");
  return {
    ...menu,
    structure: part === "food" ? out.structure : menu.structure,
    ...(part === "drinks" ? { drinks: { ...(menu.drinks ?? DEFAULT_DRINKS), structure: out.structure } } : {}),
    sections: [...food, ...drinks],
  };
}

export function newMenuInput(at: number, structure: Structure = "list", title = defaultTitle(at)): MenuInput {
  return {
    title,
    subtitle: "",
    note: "",
    structure,
    layout: structure === "list" ? "columns" : "classic",
    showNumbers: true,
    showTags: null,
    sections: [emptySection(structure === "list" ? "" : "Vorspeisen")],
  };
}

/**
 * A new menu that starts as the business's last one did (Martin, 25.10.2026, „lernen“): its style
 * and whether it shows numbers from the menu made last; its look (Klassisch, Kompakt, Zweispaltig)
 * from the last of the same form (a list or with sections), since a list looks different from a menu
 * with sections. Without menus, as `newMenuInput` makes it.
 */
export function newMenuLike(at: number, structure: Structure, menus: readonly Pick<Menu, "createdAt" | "structure" | "layout" | "showNumbers" | "style">[]): MenuInput {
  const fresh = newMenuInput(at, structure);
  const newest = (list: readonly (typeof menus)[number][]) => list.reduce<(typeof menus)[number] | null>((a, m) => (!a || m.createdAt > a.createdAt ? m : a), null);
  const last = newest(menus);
  if (!last) return fresh;
  const sameForm = newest(menus.filter((m) => m.structure === structure));
  return { ...fresh, showNumbers: last.showNumbers, ...(last.style ? { style: last.style } : {}), ...(sameForm ? { layout: sameForm.layout } : {}) };
}

/**
 * A menu as it may be stored, or what is wrong with it. Items of dishes the business does not have
 * are dropped (`isDish`); a list has exactly one section without title.
 */
export function cleanMenu(input: unknown, isDish: (id: string) => boolean): { value: MenuInput } | { error: string } {
  const o = (input ?? {}) as Record<string, unknown>;
  const title = oneLine(o.title, MENU_LIMITS.title);
  if (title === "") return { error: "Bitte einen Titel für die Speisekarte angeben." };
  const structure: Structure = o.structure === "sections" ? "sections" : "list";
  const layout: Layout = LAYOUTS.includes(o.layout as Layout) ? (o.layout as Layout) : "classic";
  const rawSections = Array.isArray(o.sections) ? o.sections.slice(0, MENU_LIMITS.sections) : [];
  let items = 0;
  const sections: MenuSection[] = rawSections.map((raw) => {
    const s = (raw ?? {}) as Record<string, unknown>;
    const kind = s.kind === "set" ? "set" : "group";
    const list = (Array.isArray(s.items) ? s.items : [])
      .map((r) => (r ?? {}) as Record<string, unknown>)
      // A subsection (S44) is an entry of its own in a section of the card, never in a set menu.
      .filter((r) => typeof r.dish === "string" && (isDish(r.dish) || (kind !== "set" && SUB_ID.test(r.dish))))
      .slice(0, Math.max(0, MENU_LIMITS.items - items))
      .map((r): MenuItem => {
        if (SUB_ID.test(r.dish as string)) return { dish: r.dish as string, price: null, note: "", sub: oneLine(r.sub, MENU_LIMITS.sub) };
        const variants = kind === "set" || !Array.isArray(r.variants) ? [] : r.variants.slice(0, 10).map((v) => (v ?? {}) as Record<string, unknown>).map((v) => ({ label: oneLine(v.label, 40), price: priceOf(v.price) })).filter((v) => v.label !== "");
        const item: MenuItem = { dish: r.dish as string, price: kind === "set" ? null : priceOf(r.price), note: oneLine(r.note, MENU_LIMITS.itemNote), ...(variants.length ? { variants } : {}) };
        if (kind !== "set") return item;
        // A set menu's courses (S32): „oder“, a surcharge, a course's title.
        const surcharge = priceOf(r.surcharge);
        const course = oneLine(r.course, MENU_LIMITS.course);
        return { ...item, ...(r.or === true ? { or: true as const } : {}), ...(r.paired === true ? { paired: true as const } : {}), ...(surcharge ? { surcharge } : {}), ...(course ? { course } : {}) };
      });
    if (kind === "set") list.splice(0, list.length, ...tidyCourses(list));
    items += list.length;
    // A set menu's further prices (S22): „Weinbegleitung 79 €“, kept only where there are any.
    const priceLabel = kind === "set" || s.part !== "drinks" ? oneLine(s.priceLabel, 40) : "";
    const setVariants =
      kind === "set" && Array.isArray(s.variants)
        ? s.variants
            .slice(0, 6)
            .map((v) => (v ?? {}) as Record<string, unknown>)
            .map((v) => ({ label: oneLine(v.label, 40), price: priceOf(v.price), allergens: Array.isArray(v.allergens) ? [...new Set(v.allergens.filter((c): c is string => typeof c === "string" && c.length <= 8))].slice(0, 20) : [] }))
            .filter((v) => v.label !== "")
        : [];
    return {
      id: typeof s.id === "string" && s.id.length <= 64 && s.id !== "" ? s.id : uuidv7(),
      title: oneLine(s.title, MENU_LIMITS.sectionTitle),
      note: oneLine(s.note, MENU_LIMITS.note),
      kind,
      price: kind === "set" ? priceOf(s.price) : null,
      items: list,
      ...(s.part === "drinks" ? { part: "drinks" as const } : {}),
      ...(priceLabel ? { priceLabel } : {}),
      ...(setVariants.length ? { variants: setVariants } : {}),
      ...(kind === "set" && s.numbers === true ? { numbers: true as const } : {}),
      ...(s.newPage === true && s.part !== "drinks" ? { newPage: true as const } : {}),
      ...(sectionTimesOf(s.web) ? { web: sectionTimesOf(s.web)! } : {}),
    };
  });
  /** A list has exactly one section without title: every item of the part in it. */
  const asList = (list: MenuSection[], part: Part): MenuSection[] => {
    const all = foldOnce(list, (i) => ({ dish: i.dish, price: null, note: i.note }));
    const first = list[0];
    return [{ id: first?.id ?? uuidv7(), title: "", note: first?.note ?? "", kind: "group", price: null, items: all, ...(part === "drinks" ? { part: "drinks" as const } : {}) }];
  };
  let food = sections.filter((s) => partOf(s) === "food");
  let drinkSections = sections.filter((s) => partOf(s) === "drinks");
  if (structure === "list") food = asList(food, "food");
  if (food.length === 0) food.push(emptySection());
  const rawDrinks = o.drinks && typeof o.drinks === "object" ? (o.drinks as Record<string, unknown>) : null;
  let drinks: DrinksPart | null = null;
  if (drinkSections.length || rawDrinks) {
    const d = rawDrinks ?? {};
    drinks = {
      structure: d.structure === "list" ? "list" : "sections",
      layout: LAYOUTS.includes(d.layout as Layout) ? (d.layout as Layout) : DEFAULT_DRINKS.layout,
      showNumbers: d.showNumbers === true,
      newPage: d.newPage === true,
    };
    if (drinks.structure === "list") drinkSections = asList(drinkSections, "drinks");
    if (drinkSections.length === 0) drinkSections.push(emptySection("Getränke", "group", "drinks"));
  }
  sections.splice(0, sections.length, ...food, ...drinkSections);
  const scale = scaleOf(o.scale);
  const ownPages = cleanOwnPages(o.ownPages);
  const showTags = Array.isArray(o.showTags) ? [...new Set(o.showTags.filter((t): t is string => typeof t === "string").map((t) => t.trim().toLowerCase()).filter(Boolean))].slice(0, 20) : null;
  return {
    value: {
      title,
      subtitle: oneLine(o.subtitle, MENU_LIMITS.subtitle),
      note: oneLine(o.note, MENU_LIMITS.note),
      structure,
      layout,
      showNumbers: o.showNumbers !== false,
      showTags,
      sections,
      style: typeof o.style === "string" ? o.style.replace(/[^\w-]/g, "").slice(0, 40) : "",
      ...(drinks ? { drinks } : {}),
      ...(scale ? { scale } : {}),
      ...(ownPages ? { ownPages } : {}),
    },
  };
}

/** The dishes and texts of a menu, by id; its subsections (S44) are neither. */
export const menuDishes = (m: Pick<Menu, "sections">) => m.sections.flatMap((s) => s.items.filter((i) => !isSubsection(i)).map((i) => i.dish));

/**
 * Where a dish stands once (C1, Martin, 25.10.2026): in each set menu („Menü“) once, and once on the
 * card besides – all the other sections together. Set menus often gather dishes that are on the card
 * too, and two menus may share a course. A scope is the id of a set menu, or null for the card.
 */
export type Scope = string | null;

/** The scope a section belongs to: a set menu its own, every other section the card's. */
export const scopeOf = (m: Pick<Menu, "sections">, sectionId: string | undefined): Scope => {
  const s = m.sections.find((x) => x.id === sectionId);
  return s?.kind === "set" ? s.id : null;
};

const inScope = (s: Pick<MenuSection, "id" | "kind">, scope: Scope) => (scope === null ? s.kind !== "set" : s.id === scope);

/** The dishes (and texts) in a scope. */
export const dishesIn = (m: Pick<Menu, "sections">, scope: Scope): Set<string> => new Set(m.sections.filter((s) => inScope(s, scope)).flatMap((s) => s.items.map((i) => i.dish)));

/** The other places a dish stands, for „auch in Menü …“: the set menus' titles, and „Karte“ for the card. */
export function placesOf(m: Pick<Menu, "sections">, dish: string, besides: Scope): string[] {
  const out: string[] = [];
  if (besides !== null && m.sections.some((s) => s.kind !== "set" && s.items.some((i) => i.dish === dish))) out.push("Karte");
  for (const s of m.sections) if (s.kind === "set" && s.id !== besides && s.items.some((i) => i.dish === dish)) out.push(s.title || "Menü");
  return out;
}

/**
 * The items of sections folded onto the card (a list, sections from categories): each dish once, at
 * its first place, with the card's price and note where it stood on the card too (C1).
 */
export function foldOnce(sections: readonly Pick<MenuSection, "kind" | "items">[], fromSet: (i: MenuItem) => MenuItem): MenuItem[] {
  const card = new Map(sections.filter((s) => s.kind !== "set").flatMap((s) => s.items.map((i) => [i.dish, i] as const)));
  const seen = new Set<string>();
  // Subsections (S44) belong to the sections they stood in: folded away.
  return sections.flatMap((s) => s.items.flatMap((i) => (isSubsection(i) || seen.has(i.dish) ? [] : (seen.add(i.dish), [card.get(i.dish) ?? (s.kind === "set" ? fromSet(i) : i)]))));
}

/** Each dish and text once, however often it stands (C1): what the counts of a menu count. */
export const uniqueItems = (m: Pick<Menu, "sections">): number => new Set(menuDishes(m)).size;
export const itemCount = (m: Pick<Menu, "sections">) => m.sections.reduce((n, s) => n + s.items.filter((i) => !isSubsection(i)).length, 0);

// ---------------------------------------------------------------------------
// The courses of a set menu (S32): a course is an item and the items after it marked „oder“.
// ---------------------------------------------------------------------------

/** The courses of a set menu's items, each a run: the first without `or`, the others its alternatives. */
export function coursesOf<I extends Pick<MenuItem, "or" | "paired">>(items: readonly I[]): I[][] {
  const out: I[][] = [];
  for (const i of items) {
    // A drink to the course (S52) stays with it, below its dishes.
    if ((i.or || i.paired) && out.length) out[out.length - 1]!.push(i);
    else out.push([i]);
  }
  return out;
}

/** How many courses a set menu has: „4 Gänge“. */
export const courseCount = (items: readonly Pick<MenuItem, "or" | "paired">[]) => coursesOf(items).length;

/** The courses kept in order: the first item never „oder“, a title only on the first item of a course. */
export function tidyCourses(items: readonly MenuItem[]): MenuItem[] {
  return items.map((i, k) => {
    // A drink to the course (S52): never the first, never „oder“, no title of its own.
    if (i.paired) {
      const rest = { ...i };
      if (k === 0) delete rest.paired;
      else delete rest.course;
      delete rest.or;
      return rest;
    }
    if (k > 0 && i.or) {
      if (i.course === undefined) return i;
      const rest = { ...i };
      delete rest.course;
      return rest;
    }
    if (!i.or) return i;
    const rest = { ...i };
    delete rest.or;
    return rest;
  });
}

/** An item as it leaves a set menu or its place: without „oder“, surcharge and the title of a course or subsection (S44). */
export function plainItem(i: MenuItem): MenuItem {
  const rest = { ...i };
  delete rest.or;
  delete rest.paired;
  delete rest.surcharge;
  delete rest.course;
  delete rest.sub;
  return rest;
}

/** The items of a section without the one at `index`: in a set menu as `withoutAt`. */
export function withoutItem(s: Pick<MenuSection, "kind" | "items">, index: number): MenuItem[] {
  return s.kind === "set" ? withoutAt(s.items, index) : s.items.filter((_, k) => k !== index);
}

/** The items of a section as printed in its subsections (S44): a run begins at each item that carries a title; the first may have none. */
export function subsectionsOf<I extends Pick<MenuItem, "sub">>(items: readonly I[]): I[][] {
  const out: I[][] = [];
  for (const i of items) {
    if (i.sub || !out.length) out.push([i]);
    else out[out.length - 1]!.push(i);
  }
  return out;
}

/**
 * The items of a set menu without the one at `index`. When it began a course, the next of the course
 * begins it now and takes its title.
 */
export function withoutAt(items: readonly MenuItem[], index: number): MenuItem[] {
  const gone = items[index];
  const next = items[index + 1];
  const out = items.filter((_, k) => k !== index);
  if (gone && !gone.or && !gone.paired && (next?.or || next?.paired)) {
    const head = { ...next, ...(gone.course && !next.course ? { course: gone.course } : {}) };
    delete head.or;
    delete head.paired;
    out[index] = head;
  }
  return out;
}

// ---------------------------------------------------------------------------
// The menu as it is shown: dishes resolved, codes in catalogue order, legend of what is used.
// ---------------------------------------------------------------------------

export interface ViewItem {
  dish: string;
  /** A text (S14): `name` is its title, `description` its text; no number, price, codes or tags. */
  text?: true;
  number: number | null;
  name: string;
  description: string;
  tags: string[];
  codes: string[];
  price: number | null;
  /** What the price is for (S19); with it the price stands on a line of its own. */
  priceLabel: string;
  /** Further prices with their own codes (S19); in a set menu without prices. */
  variants: ViewVariant[];
  note: string;
  /**
   * Prices in columns (S22): drinks one below the other with the same amounts („0,3 l | 0,5 l“) have
   * them as heads over their prices; `colHead` on the first of them, where the heads stand.
   */
  cols?: string[];
  colHead?: true;
  /**
   * Amounts as a column (S51): the labels of the prices stand right-aligned before them, the first on
   * the name's line, the others below; where a section's drinks share no head over their prices.
   */
  amounts?: true;
  /** In a set menu (S32): an alternative to the item before, with „oder“ between them. */
  or?: true;
  /** In a set menu (S52): a drink, in the drinks' look; `paired` to the course before it, below its dishes. */
  drink?: true;
  paired?: true;
  /** In a set menu (S32): the price on top of the set's. */
  surcharge?: number;
  /** In a set menu (S32): the title of the course this item begins. */
  course?: string;
  /** On a bilingual card (S36): the course's title in German and in the translation, apart („Gänge als Spalte“ sets them one below the other). */
  courseLines?: [string, string];
  /** In a section of the card (S44): the title of the subsection this item begins. */
  sub?: string;
  /** On a bilingual card (S36): the translation, a line of its own below the German. */
  trans?: string;
}

export interface ViewVariant {
  label: string;
  price: number | null;
  codes: string[];
}

export interface ViewSection {
  id: string;
  title: string;
  note: string;
  kind: "group" | "set";
  price: number | null;
  items: ViewItem[];
  /** A section of the drinks (S22). */
  part?: "drinks";
  /** A set menu's price label and further prices (S22), with their codes. */
  priceLabel?: string;
  variants?: ViewVariant[];
  /** A section of the food (S46): over the column of its prices, a figure kept with its unit („30 cm“). */
  priceHead?: string;
  /** Print, PDF and Word (S43): on a new page. */
  newPage?: true;
  /** A temporary section (S62): on the net only at these times; on paper as `print` says. */
  web?: SectionTimes;
}

/** How the drinks of a menu are shown (S22): only when it has some. */
export interface DrinksView {
  layout: Layout;
  numbered: boolean;
  newPage: boolean;
  /** The heading „Getränke“ between the parts: only when food stands above. */
  titled: boolean;
}

export interface MenuView {
  title: string;
  subtitle: string;
  note: string;
  structure: Structure;
  layout: Layout;
  numbered: boolean;
  sections: ViewSection[];
  /** The allergens and additives used on the menu, in catalogue order. */
  legend: Allergen[];
  /** The tags on the menu that have a symbol (S21), by the tag as written, in the order they first come. */
  tagIcons: Record<string, string>;
  /** How many dishes have no allergens entered (shown as a hint, never on the card). */
  unknownAllergens: number;
  /** The drinks (S22), when the menu has any. `layout` and `numbered` above are the food's. */
  drinks?: DrinksView;
  /** A card in a further language (S36, `viewIn`); German without it. */
  lang?: CardLang;
}

/** Which of a dish's tags the card shows. */
export function shownTags(tags: readonly string[], showTags: readonly string[] | null): string[] {
  return tags.filter((t) => (showTags === null ? isDietTag(t) : showTags.includes(t.toLowerCase())));
}

export function menuView(menu: MenuInput, dishes: ReadonlyMap<string, Dish>, catalog: readonly Allergen[], texts: ReadonlyMap<string, CardText> = new Map(), icons: Readonly<Record<string, string>> = {}): MenuView {
  const known = new Set(catalog.map((a) => a.code));
  const used = new Set<string>();
  let unknownAllergens = 0;
  // A set menu (S32, 12-wahlmenue.md § 11): numbers only when it has a course to choose from and
  // its own switch is on; a fixed one never, whatever the menu's switch says.
  const numbers = (s: MenuSection) => (s.kind === "set" ? s.numbers === true && s.items.some((i) => i.or) : partOf(s) === "drinks" ? (menu.drinks?.showNumbers ?? false) : menu.showNumbers);
  let carried: string | undefined;
  const sections: ViewSection[] = menu.sections.map((s) => ({
    id: s.id,
    title: s.title,
    note: s.note,
    kind: s.kind,
    price: s.price,
    ...(s.part === "drinks" ? { part: "drinks" as const } : {}),
    ...(s.newPage ? { newPage: true as const } : {}),
    ...(s.web ? { web: s.web } : {}),
    ...(s.kind === "set" && s.priceLabel ? { priceLabel: s.priceLabel } : {}),
    ...(s.kind !== "set" && s.part !== "drinks" && s.priceLabel ? { priceHead: unitSpaced(s.priceLabel) } : {}),
    ...(s.kind === "set" && s.variants?.length
      ? {
          variants: s.variants.map((v) => {
            const codes = sortCodes(v.allergens.filter((c) => known.has(c)), catalog);
            codes.forEach((c) => used.add(c));
            return { label: v.label, price: v.price, codes };
          }),
        }
      : {}),
    // A title carried over (S44) never leaves its section.
    items: ((carried = undefined), s.items).flatMap((i): ViewItem[] => {
      const d = dishes.get(i.dish);
      // A subsection's title (S44) on the item that begins it; when that one is gone from the catalogue, on the next.
      const title = s.kind === "set" ? undefined : i.sub || carried;
      carried = undefined;
      // A set menu's courses (S32): „oder“, the surcharge, the course's title.
      const course = s.kind === "set" ? { ...(i.or ? { or: true as const } : {}), ...(i.paired ? { paired: true as const } : {}), ...(i.surcharge ? { surcharge: i.surcharge } : {}), ...(i.course ? { course: i.course } : {}) } : title ? { sub: title } : {};
      if (!d) {
        const t = texts.get(i.dish);
        if (!t) carried = title;
        return t ? [{ dish: t.id, text: true, number: null, name: t.title, description: t.body, tags: [], codes: [], price: null, priceLabel: "", variants: [], note: "", ...course }] : [];
      }
      if (d.allergens === null) unknownAllergens++;
      const codes = sortCodes((d.allergens ?? []).filter((c) => known.has(c)), catalog);
      codes.forEach((c) => used.add(c));
      const set = s.kind === "set";
      // A drink in a set menu (S52) shows its prices from the catalogue, unless the menu has a further
      // price (a pairing, S22): then only that one costs.
      const drinkInSet = set && isDrink(d);
      const priced = !set || (drinkInSet && !s.variants?.length);
      const variants = itemVariants(i, d).map((v, k) => {
        const own = sortCodes(d.variants[k]!.allergens.filter((c) => known.has(c) && !codes.includes(c)), catalog);
        own.forEach((c) => used.add(c));
        return { label: v.label, price: priced ? v.price : null, codes: own };
      });
      const price = !set ? i.price : drinkInSet && priced ? d.price : null;
      return [{ dish: d.id, number: numbers(s) ? d.number : null, name: d.name, description: d.description, tags: shownTags(d.tags, menu.showTags), codes, price, priceLabel: d.priceLabel, variants, note: i.note, ...course, ...(drinkInSet ? { drink: true as const } : {}) }];
    }),
  }));
  // A course whose first item is gone from the catalogue begins with the next (S32).
  for (const s of sections) if (s.kind === "set" && s.items[0]?.or) delete s.items[0].or;
  for (const s of sections) if (s.part === "drinks" && s.kind !== "set") markColumns(s.items);
  const food = sections.filter((s) => s.part !== "drinks");
  const drinkSections = sections.filter((s) => s.part === "drinks");
  const filled = (list: ViewSection[]) => list.some((s) => s.items.length > 0);
  return {
    title: menu.title,
    subtitle: menu.subtitle,
    note: menu.note,
    structure: menu.structure,
    layout: menu.layout,
    numbered: food.some((s) => s.items.some((i) => i.number !== null)),
    sections,
    legend: catalog.filter((a) => used.has(a.code)),
    tagIcons: Object.fromEntries(sections.flatMap((s) => s.items.flatMap((i) => i.tags)).filter((t) => icons[t]).map((t) => [t, icons[t]!])),
    unknownAllergens,
    ...(filled(drinkSections)
      ? {
          drinks: {
            layout: menu.drinks?.layout ?? DEFAULT_DRINKS.layout,
            numbered: drinkSections.some((s) => s.items.some((i) => i.number !== null)),
            newPage: menu.drinks?.newPage ?? false,
            titled: filled(food),
          },
        }
      : {}),
  };
}

/** At most this many columns of prices (S22); more amounts stand in lines below the name. */
export const MAX_PRICE_COLUMNS = 4;
/** The longest label a column of amounts takes (S51): „Glas 0,1 l“, „1,5 l Magnum“; a longer one keeps its line. */
export const AMOUNT_CHARS = 16;

/** The key of an amount, for its column: its millilitres („0,5 l“ = „500 ml“), else its words. */
const amountKey = (label: string): string => {
  const ml = mlOf(label);
  return ml === null ? label.toLocaleLowerCase("de-DE").replace(/\s+/g, " ").trim() : `${ml} ml`;
};

/**
 * Prices in columns (S22). One head for the whole section (B5, Martin, 25.10.2026): when every drink
 * in it gives its amounts in millilitres and together they are at most four, the heads are all of
 * them, smallest first, and a drink without one of the amounts has an empty cell there – as long as
 * at least half of the cells have a price (S29); a sparse grid reads worse than lines.
 * Otherwise the amounts of the whole section stand in a column before the prices (S51, Martin,
 * 30.10.2026): one form for a section, no runs with changing heads. A drink keeps its lines when a
 * label is longer than a column takes or its further prices bring codes of their own (the codes
 * would have no place in a column); a drink without a label has its price alone.
 */
export function markColumns(items: ViewItem[]): void {
  const drinks = items.filter((it) => !it.text);
  const labelsOf = (it: ViewItem) => (!it.text && it.priceLabel && !it.variants.some((v) => v.codes.length) ? [it.priceLabel, ...it.variants.map((v) => v.label)] : null);
  const amounts = new Map<string, { label: string; ml: number }>();
  const whole =
    drinks.length >= 2 &&
    drinks.every((it) => {
      const labels = labelsOf(it);
      if (!labels || labels.some((l) => mlOf(l) === null) || new Set(labels.map(amountKey)).size < labels.length) return false;
      for (const l of labels) if (!amounts.has(amountKey(l))) amounts.set(amountKey(l), { label: l, ml: mlOf(l)! });
      return true;
    }) &&
    amounts.size <= MAX_PRICE_COLUMNS &&
    drinks.reduce((n, it) => n + labelsOf(it)!.length, 0) * 2 >= drinks.length * amounts.size;
  if (whole) {
    const heads = [...amounts.values()].sort((a, b) => a.ml - b.ml).map((a) => a.label);
    drinks.forEach((it, k) => {
      it.cols = heads;
      if (k === 0) it.colHead = true;
    });
    return;
  }
  for (const it of drinks) if (labelsOf(it)?.every((l) => l.length <= AMOUNT_CHARS)) it.amounts = true;
}

/** The prices of an item in columns, in the order of its heads; null where it has no such amount. */
export function columnPrices(i: { price: number | null; priceLabel?: string; variants?: readonly { label: string; price: number | null }[]; cols?: readonly string[] }): (number | null)[] {
  const all = [{ label: i.priceLabel ?? "", price: i.price }, ...(i.variants ?? [])];
  if (!i.cols) return all.map((x) => x.price);
  return i.cols.map((c) => all.find((x) => amountKey(x.label) === amountKey(c))?.price ?? null);
}

type Priced = { price: number | null; variants?: readonly { price: number | null }[]; surcharge?: number | null };
/**
 * Whether a menu shows a price anywhere: without one, no „Alle Preise in Euro …“ under it (S24), in
 * print, PDF, Word and on the web (B1, 25.10.2026).
 */
export const showsPrices = (v: { sections: readonly (Priced & { items: readonly Priced[] })[] }): boolean =>
  v.sections.some((s) => [s, ...s.items].some((x) => x.price !== null || Boolean(x.surcharge) || (x.variants ?? []).some((y) => y.price !== null)));

/** How many dishes and drinks a menu has, and its texts (S22). */
export function countsOf(menu: Pick<Menu, "sections">, dishes: ReadonlyMap<string, Dish>): { food: number; drinks: number; texts: number } {
  let [food, drinks, texts] = [0, 0, 0];
  // A dish in a set menu and on the card counts once (C1).
  for (const id of new Set(menuDishes(menu))) {
    const d = dishes.get(id);
    if (!d) texts++;
    else if (isDrink(d)) drinks++;
    else food++;
  }
  return { food, drinks, texts };
}

/** Drinks that count as alcohol-free or as alcoholic for the rule of § 6 GastG, by tag and category. */
const ALCOHOL_FREE = /alkoholfrei|softdrink|saft|säfte|wasser|limonade|schorle|heißgetränk|heissgetränk|kaffee|tee\b|kakao/i;
const ALCOHOLIC = /bier|wein|sekt|spirituos|aperitif|digestif|cocktail|longdrink|schnaps|brand|likör|cider|cidre|gin\b|rum\b|whisk|grappa|sangr/i;

/**
 * The rule of § 6 of the German Gaststättengesetz (S22): at least one alcohol-free drink may not cost
 * more than the cheapest alcoholic one, for the same amount (here per litre). A hint, never a lock;
 * null when the menu keeps it or has nothing to compare (no alcoholic drink with an amount).
 */
export function youthHint(menu: Pick<Menu, "sections">, dishes: ReadonlyMap<string, Dish>): string | null {
  let cheapestAlcohol: { name: string; perLitre: number } | null = null;
  let cheapestFree: { name: string; perLitre: number } | null = null;
  for (const s of menu.sections) {
    if (s.kind === "set") continue;
    for (const i of s.items) {
      const d = dishes.get(i.dish);
      if (!d || !isDrink(d)) continue;
      const free = d.tags.some((t) => /alkoholfrei/i.test(t)) || (ALCOHOL_FREE.test(d.category) && !ALCOHOLIC.test(d.category)) || /alkoholfrei|0,0 ?%/i.test(d.name);
      const alcoholic = !free && ALCOHOLIC.test(d.category);
      if (!free && !alcoholic) continue;
      const lines = [...(d.priceLabel ? [{ label: d.priceLabel, price: i.price }] : []), ...itemVariants(i, d)];
      for (const l of lines) {
        const ml = mlOf(l.label);
        if (!ml || l.price === null) continue;
        const perLitre = (l.price * 1000) / ml;
        const name = `${d.name} ${l.label}`;
        if (free && (!cheapestFree || perLitre < cheapestFree.perLitre)) cheapestFree = { name, perLitre };
        if (alcoholic && (!cheapestAlcohol || perLitre < cheapestAlcohol.perLitre)) cheapestAlcohol = { name, perLitre };
      }
    }
  }
  if (!cheapestAlcohol) return null;
  const litre = (c: number) => `${(c / 100).toFixed(2).replace(".", ",")} € je Liter`;
  if (!cheapestFree) return `Jugendschutz (§ 6 GastG): Auf der Speisekarte steht kein alkoholfreies Getränk mit Menge und Preis. Mindestens eines darf nicht teurer sein als das billigste alkoholische (${cheapestAlcohol.name}, ${litre(cheapestAlcohol.perLitre)}).`;
  if (cheapestFree.perLitre <= cheapestAlcohol.perLitre) return null;
  return `Jugendschutz (§ 6 GastG): Das billigste alkoholfreie Getränk (${cheapestFree.name}, ${litre(cheapestFree.perLitre)}) ist teurer als das billigste alkoholische (${cheapestAlcohol.name}, ${litre(cheapestAlcohol.perLitre)}). Mindestens ein alkoholfreies darf nicht teurer sein.`;
}

/** „a Glutenhaltiges Getreide · c Eier“ and „13 geschwärzt“, each kind in its own line. */
export function legendLines(legend: readonly Allergen[]): { allergens: string; additives: string } {
  const line = (kind: Allergen["kind"]) =>
    legend
      .filter((a) => a.kind === kind)
      .map((a) => `${a.code} ${a.name}`)
      .join(" · ");
  return { allergens: line("allergen"), additives: line("additive") };
}

// ---------------------------------------------------------------------------
// Working on a menu
// ---------------------------------------------------------------------------

/** The further prices of a dish as a menu shows them: the menu's own where it has one, else the catalogue's. */
export function itemVariants(item: Pick<MenuItem, "variants">, dish: Pick<Dish, "variants">): MenuVariant[] {
  return dish.variants.map((v) => {
    const own = item.variants?.find((x) => sameLabel(x.label, v.label));
    return { label: v.label, price: own ? own.price : v.price };
  });
}

/** The catalogue's further prices, to start a menu item with. */
const catalogVariants = (d: Pick<Dish, "variants"> | undefined): { variants?: MenuVariant[] } => (d?.variants.length ? { variants: d.variants.map((v) => ({ label: v.label, price: v.price })) } : {});

/** A dish as it goes into a section that shows prices: with the catalogue's prices. */
export const catalogItem = (i: MenuItem, d: Dish | undefined): MenuItem => ({ dish: i.dish, price: d?.price ?? null, note: i.note, ...catalogVariants(d) });

/** A price of the menu that differs from the catalogue: the main one (`to` set) and the further ones that differ. */
export interface PriceDiff {
  dish: string;
  name: string;
  number: number;
  from: number | null;
  /** The menu's main price; null: the main price is the same (or not shown). */
  to: number | null;
  variants: { label: string; from: number | null; to: number }[];
}

/** Prices of the menu that differ from the catalogue, once per dish (the first place it stands). */
export function priceDiffs(menu: Pick<Menu, "sections">, dishes: ReadonlyMap<string, Dish>): PriceDiff[] {
  const seen = new Set<string>();
  const out: PriceDiff[] = [];
  for (const s of menu.sections) {
    if (s.kind === "set") continue;
    for (const i of s.items) {
      const d = dishes.get(i.dish);
      if (!d || seen.has(d.id)) continue;
      seen.add(d.id);
      const to = i.price !== null && i.price !== d.price ? i.price : null;
      const variants = itemVariants(i, d).flatMap((v, k) => (v.price !== null && v.price !== d.variants[k]!.price ? [{ label: v.label, from: d.variants[k]!.price, to: v.price }] : []));
      if (to !== null || variants.length) out.push({ dish: d.id, name: d.name, number: d.number, from: d.price, to, variants });
    }
  }
  return out;
}

/**
 * Numbers typed into the search field: „12“, „1 4 8-12“, „3, 5, 7“. Null when it is anything else
 * (then it is a search). Ranges up to 200 numbers.
 */
export function parseNumbers(text: string): number[] | null {
  return parseNumberGroups(text)?.flat() ?? null;
}

/**
 * The numbers typed, in groups: a slash means „oder“ (S32), „21/22 30 41/42/43“ is three courses of a
 * set menu; a range alone is a course for each number. Each number once, in the group it first comes.
 */
export function parseNumberGroups(text: string): number[][] | null {
  const t = text.trim().replace(/\s*([-/])\s*/g, "$1");
  const one = "\\d+(-\\d+)?";
  if (!new RegExp(`^${one}(/${one})*([\\s,;]+${one}(/${one})*)*[\\s,;]*$`).test(t)) return null;
  const seen = new Set<number>();
  const groups: number[][] = [];
  for (const token of t.split(/[\s,;]+/).filter(Boolean)) {
    const group: number[] = [];
    for (const part of token.split("/")) {
      const m = /^(\d+)-(\d+)$/.exec(part);
      const [a, b] = m ? [Number(m[1]), Number(m[2])] : [Number(part), Number(part)];
      const [lo, hi] = a <= b ? [a, b] : [b, a];
      for (let n = lo; n <= Math.min(hi, lo + 199); n++) {
        if (seen.has(n)) continue;
        seen.add(n);
        group.push(n);
      }
    }
    // Only a slash joins: „20-24“ alone is five courses, as five numbers typed one by one.
    if (token.includes("/")) groups.push(group);
    else groups.push(...group.map((n) => [n]));
  }
  return groups.filter((g) => g.length > 0);
}

const itemFor = (d: Dish, target: Pick<MenuSection, "kind">): MenuItem => (target.kind === "set" ? { dish: d.id, price: null, note: "" } : catalogItem({ dish: d.id, price: null, note: "" }, d));

/**
 * Adds dishes at the end of a section (the last one when none is named), each once on the menu.
 * Drinks (S22) go into the part of the drinks: into the named section when it is one of the drinks,
 * else into the section titled like the drink's category (made when missing), in a list into its one
 * section. The part is made with the first drink, built as `drinks` says.
 */
export function addDishes<M extends Pick<Menu, "sections"> & { drinks?: DrinksPart }>(menu: M, dishes: readonly Dish[], sectionId?: string, drinks: DrinksPart = DEFAULT_DRINKS, drinkOrder: readonly string[] = [], ors: ReadonlySet<string> = new Set()): M {
  // Each once in the scope it goes to (C1): the set menu chosen, else the card.
  const fresh = (list: readonly Dish[], scope: Scope) => {
    const on = dishesIn(menu, scope);
    return list.filter((d) => !on.has(d.id) && (on.add(d.id), true));
  };
  let sections = menu.sections;
  const foodSections = sections.filter((s) => partOf(s) === "food");
  const target = foodSections.find((s) => s.id === sectionId) ?? foodSections[foodSections.length - 1];
  const food = target ? fresh(dishes.filter((d) => !isDrink(d)), scopeOf(menu, target.id)) : [];
  // Into a set menu a dish in `ors` is an alternative to the one before it (S32, „21/22“): a new course otherwise.
  const join = (d: Dish, item: MenuItem): MenuItem => (target?.kind === "set" && ors.has(d.id) ? { ...item, or: true } : item);
  if (target && food.length) sections = sections.map((s) => (s === target ? { ...s, items: s.kind === "set" ? tidyCourses([...s.items, ...food.map((d) => join(d, itemFor(d, target)))]) : [...s.items, ...food.map((d) => itemFor(d, target))] } : s));
  // Drinks into a set menu chosen (S52): at its end, to its last course.
  const set = sections.find((s) => s.id === sectionId && s.kind === "set" && partOf(s) === "food");
  if (set) {
    const into = fresh(dishes.filter(isDrink), scopeOf(menu, set.id));
    if (into.length) sections = sections.map((s) => (s.id === set.id ? { ...s, items: tidyCourses([...s.items, ...into.map((d) => ({ dish: d.id, price: null, note: "", paired: true as const }))]) } : s));
    return { ...menu, sections };
  }
  const list = sections.filter((s) => partOf(s) === "drinks").map((s) => ({ ...s, items: [...s.items] }));
  const chosen = list.find((s) => s.id === sectionId);
  const drinkList = fresh(dishes.filter(isDrink), chosen ? scopeOf(menu, chosen.id) : null);
  if (!drinkList.length) return { ...menu, sections };
  const part = menu.drinks ?? drinks;
  for (const d of drinkList) {
    let into = chosen;
    if (!into && part.structure === "list") into = list[0];
    if (!into) {
      const top = topCategory(d.category);
      into = top ? list.find((s) => s.kind !== "set" && s.title.toLocaleLowerCase("de-DE") === top.toLocaleLowerCase("de-DE")) : undefined;
      // The one empty section the part began with takes the first drink's category as its title.
      if (!into && list.length === 1 && list[0]!.items.length === 0 && list[0]!.kind !== "set") {
        into = list[0]!;
        if (top) into.title = top;
      }
      if (!into && !top) into = list[list.length - 1];
    }
    if (!into) {
      into = emptySection(part.structure === "list" ? "" : topCategory(d.category) || "Getränke", "group", "drinks");
      // At the place the list of drink categories gives it, as „Abschnitte aus Kategorien“ (B4, 25.10.2026).
      const rank = categoryRank(drinkOrder);
      const own = rank.ofTop(into.title);
      const at = list.findIndex((s) => s.kind !== "set" && rank.ofTop(s.title) > own);
      list.splice(at < 0 ? list.length : at, 0, into);
    }
    into.items.push(itemFor(d, into));
  }
  return { ...menu, drinks: { ...part }, sections: [...sections.filter((s) => partOf(s) === "food"), ...list] };
}

/** Takes a dish off the menu: wherever it stands, or only in one scope (C1: that set menu, or the card). */
export function removeDish<M extends Pick<Menu, "sections">>(menu: M, dish: string, scope?: Scope): M {
  const without = (s: MenuSection): MenuItem[] => {
    const at = s.items.findIndex((i) => i.dish === dish);
    if (at < 0) return s.items;
    return withoutItem(s, at);
  };
  return { ...menu, sections: menu.sections.map((s) => (scope !== undefined && !inScope(s, scope) ? s : { ...s, items: without(s) })) };
}

export type SortKey = "number" | "name" | "price" | "category";
export const SORT_LABELS: Record<SortKey, string> = { number: "Nummer", name: "Name", price: "Preis", category: "Kategorie" };

/**
 * Sorts the dishes of every section (or of one); by category in the order of the business's list.
 * Texts (S14) keep their places: „Letzte Bestellung …“ stays at the end.
 */
export function sortItems<M extends Pick<Menu, "sections">>(menu: M, by: SortKey, dishes: ReadonlyMap<string, Dish>, sectionId?: string, order: readonly string[] = []): M {
  const rank = categoryRank(order);
  const key = (i: MenuItem) => dishes.get(i.dish)!;
  const cmp = (a: MenuItem, b: MenuItem) => {
    const x = key(a);
    const y = key(b);
    if (by === "name") return x.name.localeCompare(y.name, "de");
    if (by === "price") return (a.price ?? x.price ?? 0) - (b.price ?? y.price ?? 0) || x.number - y.number;
    if (by === "category") return rank.of(x.category) - rank.of(y.category) || x.category.localeCompare(y.category, "de") || x.number - y.number;
    return x.number - y.number;
  };
  const sorted = (items: MenuItem[]) => {
    const list = items.filter((i) => dishes.has(i.dish)).sort(cmp);
    return items.map((i) => (dishes.has(i.dish) ? list.shift()! : i));
  };
  // In a set menu the courses keep their order; only the alternatives of a course are sorted (S32).
  const inCourses = (items: MenuItem[]) =>
    coursesOf(items).flatMap((course) => {
      const head = course[0]!;
      return sorted(course).map((i, k) => {
        const rest = plainItem(i);
        return { ...rest, ...(i.surcharge ? { surcharge: i.surcharge } : {}), ...(k > 0 ? { or: true as const } : head.course ? { course: head.course } : {}) };
      });
    });
  // In a section with subsections (S44) the dishes are sorted within each; the subsections keep their places.
  const inSubsections = (items: MenuItem[]) => {
    const out: MenuItem[] = [];
    let run: MenuItem[] = [];
    for (const i of [...items, null]) {
      if (i && !isSubsection(i)) run.push(i);
      else {
        out.push(...sorted(run), ...(i ? [i] : []));
        run = [];
      }
    }
    return out;
  };
  return { ...menu, sections: menu.sections.map((s) => (sectionId && s.id !== sectionId ? s : { ...s, items: s.kind === "set" ? inCourses(s.items) : inSubsections(s.items) })) };
}

/**
 * Moves an item within the menu: to another place in its section or into another section. A dish
 * stays in its part (S22); a text may go anywhere – when `dishes` tells them apart, else nothing
 * crosses.
 */
export function moveItem<M extends Pick<Menu, "sections">>(menu: M, from: { section: string; index: number }, to: { section: string; index: number }, dishes?: ReadonlyMap<string, unknown>): M {
  const source = menu.sections.find((s) => s.id === from.section);
  const item = source?.items[from.index];
  if (!source || !item) return menu;
  // A dish stays in its part (S22): no drink among the food, no dish among the drinks; only a set menu
  // takes drinks too (S52), from the drinks or from another set menu.
  const target = menu.sections.find((s) => s.id === to.section);
  if (!target) return menu;
  const known = dishes?.get(item.dish);
  const drink = known !== undefined && isDrink(known as Pick<Dish, "kind">);
  if (drink ? partOf(target) === "food" && target.kind !== "set" : partOf(target) !== partOf(source) && (!dishes || dishes.has(item.dish))) return menu;
  // A subsection (S44) stays on the card, never in a set menu.
  if (isSubsection(item) && target.kind === "set") return menu;
  // Not twice in one scope (C1): into a set menu that has it, or onto the card from a set menu when the card has it.
  const there = scopeOf(menu, target.id);
  if (there !== scopeOf(menu, source.id) && dishesIn(menu, there).has(item.dish)) return menu;
  const removed = menu.sections.map((s) => (s.id === from.section ? { ...s, items: withoutItem(s, from.index) } : s));
  return {
    ...menu,
    sections: removed.map((s) => {
      if (s.id !== to.section) return s;
      const items = [...s.items];
      const at = Math.max(0, Math.min(to.index, items.length));
      if (s.kind !== "set") {
        items.splice(at, 0, isSubsection(item) ? item : { ...plainItem(item), price: item.price ?? null });
        return { ...s, items };
      }
      // Into a set menu (S32): between two alternatives it is one more, else a course of its own. Its
      // surcharge goes with it; a course's title stays with the course.
      const joins = Boolean(items[at]?.or);
      // A drink (S52) goes to the course before it, unless it was a course of its own and stays one.
      const pairs = drink && at > 0 && (source.kind !== "set" || Boolean(item.paired));
      items.splice(at, 0, { dish: item.dish, price: null, note: item.note, ...(item.surcharge ? { surcharge: item.surcharge } : {}), ...(pairs ? { paired: true as const } : joins ? { or: true as const } : {}) });
      return { ...s, items: tidyCourses(items) };
    }),
  };
}

/** One section per first-level category (Kalt, Warm, Süß), in the order of the business's list. */
export function sectionsFromCategories<M extends Pick<Menu, "sections" | "structure">>(menu: M, dishes: ReadonlyMap<string, Dish>, order: readonly string[] = []): M {
  const rank = categoryRank(order);
  const items = foldOnce(menu.sections, (i) => catalogItem(i, dishes.get(i.dish)));
  const groups = new Map<string, MenuItem[]>();
  for (const i of items.filter((x) => dishes.has(x.dish))) {
    const top = topCategory(dishes.get(i.dish)?.category ?? "") || "Weitere";
    groups.set(top, [...(groups.get(top) ?? []), i]);
  }
  const sections = [...groups].sort(([a], [b]) => rank.ofTop(a) - rank.ofTop(b)).map(([title, list]) => ({ ...emptySection(title), items: list }));
  // Texts at the end of the last section, in their order.
  const texts = items.filter((x) => !dishes.has(x.dish));
  if (texts.length) {
    if (!sections.length) sections.push(emptySection("Hinweise"));
    sections[sections.length - 1]!.items.push(...texts);
  }
  return { ...menu, structure: "sections", sections: sections.length ? sections : [emptySection("Vorspeisen")] };
}

/**
 * Subsections from the second level of the categories (S44) in one section: „Carne / Kalb“, „Carne /
 * Rind“ become „Kalb“ and „Rind“, in the order of the business's list. Dishes without a second level
 * come first, without a title; texts stay at the end. The titles are a start, to be changed („Vom Kalb“).
 */
export function subsectionsFromCategories<M extends Pick<Menu, "sections">>(menu: M, sectionId: string, dishes: ReadonlyMap<string, Dish>, order: readonly string[] = [], at = Date.now()): M {
  const rank = categoryRank(order);
  return {
    ...menu,
    sections: menu.sections.map((s) => {
      if (s.id !== sectionId || s.kind === "set") return s;
      const plain = s.items.filter((i) => !isSubsection(i));
      const groups = new Map<string, MenuItem[]>();
      for (const i of plain.filter((x) => dishes.has(x.dish))) {
        const second = secondCategory(dishes.get(i.dish)!.category);
        groups.set(second, [...(groups.get(second) ?? []), i]);
      }
      const ranked = [...groups].sort(([a, x], [b, y]) => (a === "" ? -1 : b === "" ? 1 : rank.of(dishes.get(x[0]!.dish)!.category) - rank.of(dishes.get(y[0]!.dish)!.category) || a.localeCompare(b, "de")));
      const items = ranked.flatMap(([title, list], k) => [...(title ? [newSubsection(title, at + k)] : []), ...list]);
      return { ...s, items: [...items, ...plain.filter((x) => !dishes.has(x.dish))] };
    }),
  };
}

/** The second levels of the categories of a section's dishes (S44): from two on, subsections are worth it. */
export const secondLevelsIn = (s: Pick<MenuSection, "items">, dishes: ReadonlyMap<string, Pick<Dish, "category">>) => new Set(s.items.map((i) => secondCategory(dishes.get(i.dish)?.category ?? "")).filter(Boolean));

/** A list again: every item in one section, in the order they stand. */
export function flatten<M extends Pick<Menu, "sections" | "structure">>(menu: M, dishes: ReadonlyMap<string, Dish>): M {
  const items = foldOnce(menu.sections, (i) => catalogItem(i, dishes.get(i.dish)));
  return { ...menu, structure: "list", sections: [{ ...emptySection(""), id: menu.sections[0]?.id ?? uuidv7(), items }] };
}

/** A copy to start a new menu from: new ids for the sections, the same dishes and prices. */
export function copyOf(menu: MenuInput, at: number): MenuInput {
  return { ...menu, title: `${menu.title} (Kopie)`.slice(0, MENU_LIMITS.title), sections: menu.sections.map((s) => ({ ...s, id: uuidv7(at), items: s.items.map((i) => ({ ...i, ...(i.variants ? { variants: i.variants.map((v) => ({ ...v })) } : {}) })) })) };
}

/** The prices on a menu set to the catalogue's (after the catalogue changed). */
export function withCatalogPrices<M extends Pick<Menu, "sections">>(menu: M, dishes: ReadonlyMap<string, Dish>): M {
  const catalog = (i: MenuItem): MenuItem => {
    const d = dishes.get(i.dish);
    return d ? { ...catalogItem(i, d), price: d.price ?? i.price } : i;
  };
  return { ...menu, sections: menu.sections.map((s) => (s.kind === "set" ? s : { ...s, items: s.items.map(catalog) })) };
}
